'use client';

/**
 * Unified storefront price display.
 *
 * Renders one of these forms based on what's in `pricing`:
 *
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ Default     │ ₪45                                                  │
 *   │ Range       │ ₪30 – ₪60                                            │
 *   │ Sale        │ ~~₪45~~  ₪35   [-22%]                                │
 *   │ Sale+Range  │ ~~₪40–₪70~~  ₪30–₪60   [-22%]                        │
 *   │ Tiers       │ ₪45                                                  │
 *   │             │ Starting from ₪40                                    │
 *   │ Default+    │ (any of the above) + tier sub-line                   │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * Price-group resolution (wholesale, customer-specific pricing) happens
 * UPSTREAM at data-fetch time. The component just renders whatever
 * numbers it's given — the caller sets `pricing.base` to the
 * resolved price for the current user.
 *
 * Sale precedence: if `sale.current < sale.original`, the sale form wins.
 */

import { useCurrency } from '@/lib/currency/CurrencyContext';
import { formatPrice } from '@/lib/currency/formatPrice';
import { cn } from '@/lib/utils';

export type PriceTier = { min_qty: number; price: number };

export type PriceData = {
  /** Base/regular price in ILS. Always required. Already resolved for the user's price group if applicable. */
  base: number;
  /** Active sale: `current` displayed, `original` shown as strikethrough. */
  sale?: { current: number; original: number } | null;
  /** Variant price range — for variable products with non-uniform variant prices. */
  range?: { min: number; max: number } | null;
  /** Volume/wholesale tiers — surfaces "Starting from {lowest tier price}" sub-line. */
  tiers?: PriceTier[] | null;
  /**
   * `base` is the cheapest of several differing prices, not the price. Renders
   * "Starting from X" so a variable product whose variants run 340–460 stops
   * advertising a flat 340.
   */
  startingFrom?: boolean;
};

type Size = 'sm' | 'md' | 'lg';

type Props = {
  pricing: PriceData;
  /** Locale — drives the "Starting from" / "بدءًا من" tier sub-line. */
  locale: string;
  size?: Size;
  /** Hide the "−X%" badge when on sale (default: shown). */
  showPercentBadge?: boolean;
  /** Hide the "Starting from" sub-line when tiers present (default: shown). */
  showTiersHint?: boolean;
  className?: string;
};

const SIZE_CLASSES: Record<Size, { primary: string; secondary: string; badge: string }> = {
  sm: {
    primary: 'text-sm font-semibold',
    secondary: 'text-xs',
    badge: 'text-[10px] px-1.5 py-0.5',
  },
  md: {
    primary: 'text-base font-semibold',
    secondary: 'text-sm',
    badge: 'text-xs px-2 py-0.5',
  },
  lg: {
    primary: 'text-2xl font-bold',
    secondary: 'text-sm',
    badge: 'text-xs px-2 py-1',
  },
};

export function Price({
  pricing,
  locale,
  size = 'md',
  showPercentBadge = true,
  showTiersHint = true,
  className,
}: Props) {
  const { currency } = useCurrency();
  const sizeCls = SIZE_CLASSES[size];

  const onSale =
    pricing.sale != null &&
    pricing.sale.current > 0 &&
    pricing.sale.current < pricing.sale.original;

  const hasRange =
    pricing.range != null && pricing.range.max > pricing.range.min;

  const lowestTierPrice =
    pricing.tiers && pricing.tiers.length > 0
      ? Math.min(...pricing.tiers.map((t) => t.price))
      : null;

  const showTierLine =
    showTiersHint &&
    lowestTierPrice != null &&
    lowestTierPrice < (onSale ? pricing.sale!.current : pricing.base);

  const fromLabel = locale === 'ar' ? 'بدءًا من' : 'Starting from';

  // Format the primary line — possibly a range
  const formatRange = (min: number, max: number) =>
    `${formatPrice(min, currency)} – ${formatPrice(max, currency)}`;

  let primary: string;
  let strikethrough: string | null = null;

  if (onSale) {
    if (hasRange) {
      // Sale + range — assume the sale shifts the whole range proportionally.
      // If only `sale.current` is present (single number), show that as the
      // current and the original range as strikethrough.
      strikethrough = formatRange(pricing.range!.min, pricing.range!.max);
      primary = formatPrice(pricing.sale!.current, currency);
    } else {
      strikethrough = formatPrice(pricing.sale!.original, currency);
      primary = formatPrice(pricing.sale!.current, currency);
    }
  } else if (hasRange) {
    primary = formatRange(pricing.range!.min, pricing.range!.max);
  } else if (pricing.startingFrom) {
    primary = `${fromLabel} ${formatPrice(pricing.base, currency)}`;
  } else {
    primary = formatPrice(pricing.base, currency);
  }

  const percentOff = onSale
    ? Math.round(
        ((pricing.sale!.original - pricing.sale!.current) / pricing.sale!.original) * 100
      )
    : 0;

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-baseline gap-2 flex-wrap">
        {strikethrough && (
          <span
            className={cn(
              sizeCls.secondary,
              'text-muted-foreground line-through'
            )}
          >
            {strikethrough}
          </span>
        )}
        <span data-price-primary="" className={cn(sizeCls.primary, 'text-foreground')}>{primary}</span>
        {onSale && showPercentBadge && percentOff > 0 && (
          <span
            className={cn(
              sizeCls.badge,
              'rounded-full bg-destructive/10 text-destructive font-semibold'
            )}
          >
            −{percentOff}%
          </span>
        )}
      </div>
      {showTierLine && (
        <span className={cn(sizeCls.secondary, 'text-muted-foreground mt-0.5')}>
          {fromLabel} {formatPrice(lowestTierPrice!, currency)}
        </span>
      )}
    </div>
  );
}
